// src/utils/paystackInline.js
// Thin wrapper around Paystack's hosted inline.js. The script is injected
// once on first use; subsequent calls reuse `window.PaystackPop`. In mock
// mode (dev without PAYSTACK_PUBLIC_KEY) we short-circuit to a fake success
// after a 600ms delay so the rest of the flow can be tested end-to-end.

const SCRIPT_URL = 'https://js.paystack.co/v1/inline.js';
let scriptPromise = null;

const ensureScript = () => {
	if (typeof window === 'undefined') return Promise.resolve();
	if (window.PaystackPop) return Promise.resolve();
	if (scriptPromise) return scriptPromise;
	scriptPromise = new Promise((resolve, reject) => {
		const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`);
		if (existing) {
			existing.addEventListener('load', () => resolve());
			existing.addEventListener('error', () => reject(new Error('Paystack script failed to load')));
			if (window.PaystackPop) resolve();
			return;
		}
		const tag = document.createElement('script');
		tag.src = SCRIPT_URL;
		tag.async = true;
		tag.onload = () => resolve();
		tag.onerror = () => reject(new Error('Paystack script failed to load'));
		document.head.appendChild(tag);
	});
	return scriptPromise;
};

// Open the Paystack popup. Resolves with { status, reference } when the
// guest completes or cancels. `mock` skips the popup and resolves
// success synthetically — used when the server is in mock mode.
//
// params:
//   publicKey   — pk_test_… / pk_live_…
//   email       — guest email (server falls back to a synthetic one)
//   amountKobo  — total amount in kobo
//   reference   — server-generated unique ref
//   accessCode  — from /transaction/initialize
//   mock        — if true, resolve success after 600ms without opening UI
export const openPaystackCheckout = async ({
	publicKey,
	email,
	amountKobo,
	reference,
	accessCode,
	mock = false,
	metadata = {},
}) => {
	if (mock) {
		await new Promise((r) => setTimeout(r, 600));
		return { status: 'success', reference, mock: true };
	}
	if (!publicKey) {
		throw new Error('Paystack public key is not configured.');
	}
	await ensureScript();
	if (!window.PaystackPop) {
		throw new Error('Paystack popup is not available.');
	}
	return new Promise((resolve) => {
		const handler = window.PaystackPop.setup({
			key: publicKey,
			email,
			amount: amountKobo,
			ref: reference,
			access_code: accessCode || undefined,
			currency: 'NGN',
			metadata,
			callback: (response) => {
				resolve({ status: 'success', reference: response?.reference || reference });
			},
			onClose: () => {
				resolve({ status: 'closed', reference });
			},
		});
		// Some Paystack versions auto-open on setup; others need iframe().
		if (handler && typeof handler.openIframe === 'function') {
			handler.openIframe();
		}
	});
};

export default openPaystackCheckout;
